import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { profile } from '../lib/api';
import { User, Mail, Shield, Calendar, Camera, Save, Lock, Eye, EyeOff } from 'lucide-react';
import { format } from 'date-fns';

const Profile = () => {
  const { user } = useAuth();
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    profile_image_url: user?.profile_image_url || ''
  });
  const [passwordData, setPasswordData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });

  const handleSave = async () => {
    setSaving(true);
    try {
      await profile.updateProfile(formData);
      alert('Profile updated successfully!');
      setEditing(false);
      window.location.reload(); // Refresh to update user context
    } catch (error) {
      console.error('Failed to update profile:', error);
      alert('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setFormData({
      name: user?.name || '',
      profile_image_url: user?.profile_image_url || ''
    });
    setEditing(false);
  };

  const handlePasswordChange = async () => {
    // Validation
    if (!passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password) {
      alert('Please fill in all password fields');
      return;
    }

    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('New passwords do not match');
      return;
    }

    if (passwordData.new_password.length < 6) {
      alert('New password must be at least 6 characters long');
      return;
    }

    setSaving(true);
    try {
      await profile.changePassword({
        current_password: passwordData.current_password,
        new_password: passwordData.new_password
      });
      alert('Password changed successfully! Please login again with your new password.');
      setPasswordData({
        current_password: '',
        new_password: '',
        confirm_password: ''
      });
      setChangingPassword(false);
    } catch (error) {
      console.error('Failed to change password:', error);
      alert(error.response?.data?.detail || 'Failed to change password. Please check your current password.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancelPasswordChange = () => {
    setPasswordData({
      current_password: '',
      new_password: '',
      confirm_password: ''
    });
    setChangingPassword(false);
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2);
  };

  return (
    <div className="space-y-6" data-testid="profile-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-800">My Profile</h1>
        <p className="text-gray-600 mt-1">Manage your account settings</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <div className="lg:col-span-1">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex flex-col items-center">
              {/* Profile Image */}
              <div className="relative group mb-4">
                {formData.profile_image_url ? (
                  <img
                    src={formData.profile_image_url}
                    alt={user?.name}
                    className="w-32 h-32 rounded-full object-cover border-4 border-amber-500 shadow-lg"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                ) : null}
                <div 
                  className={`w-32 h-32 rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center text-white text-4xl font-bold shadow-lg border-4 border-amber-500 ${formData.profile_image_url ? 'hidden' : 'flex'}`}
                >
                  {getInitials(user?.name)}
                </div>
                {editing && (
                  <div className="absolute inset-0 bg-black/50 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition cursor-pointer">
                    <Camera className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>

              {/* User Info */}
              <h2 className="text-2xl font-bold text-gray-800">{user?.name}</h2>
              <p className="text-gray-600 mt-1">{user?.email}</p>
              <span className={`mt-3 inline-flex px-4 py-2 text-sm font-semibold rounded-full ${
                user?.role === 'Admin' ? 'bg-red-100 text-red-700' :
                user?.role === 'Ops Manager' ? 'bg-blue-100 text-blue-700' :
                user?.role === 'Accounts' ? 'bg-green-100 text-green-700' :
                'bg-purple-100 text-purple-700'
              }`}>
                <Shield className="w-4 h-4 mr-2" />
                {user?.role}
              </span>

              {!editing && (
                <button
                  onClick={() => setEditing(true)}
                  className="mt-6 w-full bg-gradient-to-r from-amber-500 to-orange-600 text-white py-2 px-4 rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow-lg"
                  data-testid="edit-profile-button"
                >
                  Edit Profile
                </button>
              )}
            </div>
          </div>
        </div>

        {/* Profile Details */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-800 mb-6">Profile Information</h3>

            <div className="space-y-6">
              {/* Name */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <User className="w-4 h-4 mr-2" />
                  Full Name
                </label>
                {editing ? (
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="Enter your name"
                  />
                ) : (
                  <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-800 font-medium">{user?.name}</p>
                )}
              </div>

              {/* Email (Read-only) */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Mail className="w-4 h-4 mr-2" />
                  Email Address
                </label>
                <p className="px-4 py-3 bg-gray-100 rounded-lg text-gray-600 font-medium">{user?.email}</p>
                <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>

              {/* Role (Read-only) */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Shield className="w-4 h-4 mr-2" />
                  Role
                </label>
                <p className="px-4 py-3 bg-gray-100 rounded-lg text-gray-600 font-medium">{user?.role}</p>
                <p className="text-xs text-gray-500 mt-1">Role is managed by administrators</p>
              </div>

              {/* Profile Image URL */}
              {editing && (
                <div>
                  <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                    <Camera className="w-4 h-4 mr-2" />
                    Profile Image URL
                  </label>
                  <input
                    type="url"
                    value={formData.profile_image_url}
                    onChange={(e) => setFormData({...formData, profile_image_url: e.target.value})}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                    placeholder="https://example.com/your-image.jpg"
                  />
                  <p className="text-xs text-gray-500 mt-1">Enter a URL to your profile picture</p>
                </div>
              )}

              {/* Account Created */}
              <div>
                <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
                  <Calendar className="w-4 h-4 mr-2" />
                  Member Since
                </label>
                <p className="px-4 py-3 bg-gray-50 rounded-lg text-gray-800 font-medium">
                  {user?.created_at ? format(new Date(user.created_at), 'MMMM dd, yyyy') : 'N/A'}
                </p>
              </div>

              {/* Action Buttons */}
              {editing && (
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCancel}
                    className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow-lg disabled:opacity-50"
                    data-testid="save-profile-button"
                  >
                    <Save className="w-4 h-4" />
                    <span>{saving ? 'Saving...' : 'Save Changes'}</span>
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Security Section - Change Password */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold text-gray-800">Security</h3>
              {!changingPassword && (
                <button
                  onClick={() => setChangingPassword(true)}
                  className="flex items-center space-x-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg font-medium hover:bg-gray-200 transition"
                  data-testid="change-password-button"
                >
                  <Lock className="w-4 h-4" />
                  <span>Change Password</span>
                </button>
              )}
            </div>

            {!changingPassword ? (
              <div>
                <p className="text-gray-600 text-sm mb-4">
                  Keep your account secure by using a strong, unique password.
                </p>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <p className="text-sm text-blue-800">
                    <strong>Tip:</strong> Use a password with at least 12 characters, including uppercase, lowercase, numbers, and symbols.
                  </p>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Current Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showCurrentPassword ? "text" : "password"}
                      value={passwordData.current_password}
                      onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Enter current password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showCurrentPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordData.new_password}
                      onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Enter new password (min 6 characters)"
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showNewPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordData.new_password && passwordData.new_password.length < 6 && (
                    <p className="text-xs text-red-600 mt-1">Password must be at least 6 characters</p>
                  )}
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Confirm New Password *
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordData.confirm_password}
                      onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                      placeholder="Confirm new password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                  {passwordData.confirm_password && passwordData.new_password !== passwordData.confirm_password && (
                    <p className="text-xs text-red-600 mt-1">Passwords do not match</p>
                  )}
                </div>

                {/* Password Strength Indicator */}
                {passwordData.new_password && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs font-medium text-gray-700 mb-2">Password Strength:</p>
                    <div className="flex space-x-1">
                      <div className={`h-2 flex-1 rounded ${passwordData.new_password.length >= 6 ? 'bg-yellow-400' : 'bg-gray-300'}`}></div>
                      <div className={`h-2 flex-1 rounded ${passwordData.new_password.length >= 8 ? 'bg-yellow-400' : 'bg-gray-300'}`}></div>
                      <div className={`h-2 flex-1 rounded ${passwordData.new_password.length >= 12 && /[A-Z]/.test(passwordData.new_password) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                      <div className={`h-2 flex-1 rounded ${passwordData.new_password.length >= 12 && /[A-Z]/.test(passwordData.new_password) && /[0-9]/.test(passwordData.new_password) && /[^A-Za-z0-9]/.test(passwordData.new_password) ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">
                      {passwordData.new_password.length < 6 ? 'Too weak' :
                       passwordData.new_password.length < 8 ? 'Weak' :
                       passwordData.new_password.length < 12 ? 'Medium' :
                       /[A-Z]/.test(passwordData.new_password) && /[0-9]/.test(passwordData.new_password) && /[^A-Za-z0-9]/.test(passwordData.new_password) ? 'Strong' : 'Medium'}
                    </p>
                  </div>
                )}

                {/* Action Buttons */}
                <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200">
                  <button
                    onClick={handleCancelPasswordChange}
                    className="px-6 py-2 border border-gray-300 rounded-lg font-medium text-gray-700 hover:bg-gray-50 transition"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handlePasswordChange}
                    disabled={saving || !passwordData.current_password || !passwordData.new_password || !passwordData.confirm_password || passwordData.new_password !== passwordData.confirm_password}
                    className="flex items-center space-x-2 px-6 py-2 bg-gradient-to-r from-amber-500 to-orange-600 text-white rounded-lg font-semibold hover:from-amber-600 hover:to-orange-700 transition shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="save-password-button"
                  >
                    <Lock className="w-4 h-4" />
                    <span>{saving ? 'Changing...' : 'Change Password'}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;
